<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores'

const router = useRouter()
const appStore = useAppStore()

// 폼 데이터
const birthDate = ref('')
const userName = ref('')

// 폼 유효성 검사
const isFormValid = computed(() => {
  return birthDate.value.length === 6 && userName.value.trim().length > 0
})

// 생년월일 포맷팅 함수
const formatBirthDate = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value.replace(/\D/g, '') // 숫자만 허용
  if (value.length > 6) {
    value = value.slice(0, 6)
  }
  birthDate.value = value
}

// 설문 시작 함수
const startSurvey = () => {
  if (isFormValid.value) {
    //Form 데이터를 스토어에 저장
    appStore.setBirthDate(birthDate.value)
    appStore.setUserName(userName.value)
    // 설문 페이지로 이동
    router.push('/survey')
  }
}
onMounted(() => {
  appStore.clearAll()
})
</script>
<template>
  <div class="index-page">
    <!-- 배경 그라데이션 -->
    <div
      class="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
    ></div>

    <!-- 메인 컨테이너 -->
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div class="w-full max-w-4xl py-8">
        <!-- 헤더 섹션 -->
        <div class="text-center mb-4">
          <div
            class="inline-flex items-center justify-center w-10 h-10 bg-primary-600 rounded-full mb-2 shadow-lg"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h1
            class="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1 leading-tight"
          >
            DiSC 직무적성 검사
          </h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mb-2 mx-auto leading-relaxed">
            당신의 업무 성향을 파악하고 더 나은 커리어를 위한 첫 걸음을 시작하세요
          </p>
          <div class="flex items-center justify-center space-x-1">
            <div class="w-6 h-1 bg-primary-600 rounded-full"></div>
            <div class="w-3 h-1 bg-primary-400 rounded-full"></div>
            <div class="w-1 h-1 bg-primary-300 rounded-full"></div>
          </div>
        </div>

        <!-- 메인 콘텐츠 카드 -->
        <div
          class="admin-card p-4 md:p-6 shadow-2xl border-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm"
        >
          <!-- 자신과 타인을 이해하는 방법 -->
          <div class="mb-4">
            <div class="flex items-center mb-3">
              <div
                class="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mr-2 shadow-lg"
              >
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">
                자신과 타인을 이해하는 방법
              </h2>
            </div>
            <div
              class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200/50 dark:border-blue-800/50 shadow-inner"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg"
                >
                  <svg
                    class="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                    DiSC 직무적성 검사는 특정상황에서 자신과 다른 사람을 이해하는 손쉬운 방법을
                    제공합니다.
                  </p>
                  <p class="text-gray-600 dark:text-gray-400 leading-relaxed mt-1 text-sm">
                    또한 시험이 아니므로 정답이나 오답이 없습니다. 그러니 부담없이 편안한 마음으로
                    응답 해 주시길 바랍니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 설문요령 -->
          <div class="mb-4">
            <div class="flex items-center mb-3">
              <div
                class="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center mr-2 shadow-lg"
              >
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">설문요령</h2>
            </div>
            <div
              class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200/50 dark:border-green-800/50 shadow-inner"
            >
              <div class="space-y-4 text-gray-700 dark:text-gray-300">
                <div class="flex items-center group">
                  <span
                    class="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 text-white rounded-full flex items-center justify-center text-base font-bold mr-4 shadow-lg group-hover:scale-110 transition-transform duration-200"
                    >1</span
                  >
                  <div class="bg-white/70 dark:bg-gray-800/70 rounded-lg p-4 flex-1 shadow-sm">
                    <p class="leading-relaxed text-sm">
                      응답지의 28개 문항에 대해 업무환경에서 보여주는 자신의 업무경향을 생각하시면서
                      응답합니다.
                    </p>
                  </div>
                </div>
                <div class="flex items-center group">
                  <span
                    class="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 text-white rounded-full flex items-center justify-center text-base font-bold mr-4 shadow-lg group-hover:scale-110 transition-transform duration-200"
                    >2</span
                  >
                  <div class="bg-white/70 dark:bg-gray-800/70 rounded-lg p-4 flex-1 shadow-sm">
                    <p class="leading-relaxed text-sm">
                      각 문항마다 4개의 단어중 자신을 가장 잘 묘사한다고 생각하는 단어를 선택하여
                      최고를 선택하고 자신과 가장 관계없다고 생각되는 단어를 선택하여 최소를
                      선택합니다. 한 문항당 반드시 최고 하나와 최소하나를 선택하셔야 합니다.
                    </p>
                  </div>
                </div>
                <div class="flex items-center group">
                  <span
                    class="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 text-white rounded-full flex items-center justify-center text-base font-bold mr-4 shadow-lg group-hover:scale-110 transition-transform duration-200"
                    >3</span
                  >
                  <div class="bg-white/70 dark:bg-gray-800/70 rounded-lg p-4 flex-1 shadow-sm">
                    <p class="leading-relaxed text-sm">한 문항당 15초가 주어집니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 응시자 정보 -->
          <div class="mb-4">
            <div class="flex items-center mb-3">
              <div
                class="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center mr-2 shadow-lg"
              >
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">응시자 정보</h2>
            </div>

            <div
              class="admin-card p-4 bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-700/50 dark:to-slate-700/50 border-0 shadow-lg"
            >
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- 생년월일 입력 -->
                <div class="space-y-2">
                  <label
                    class="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    <svg
                      class="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    생년월일(6자리)
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                  <div class="relative">
                    <input
                      type="text"
                      v-model="birthDate"
                      placeholder="930819"
                      class="form-input pl-10 pr-10 py-3 text-base font-medium tracking-wider"
                      maxlength="6"
                      @input="formatBirthDate"
                    />
                    <div
                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    >
                      <svg
                        class="w-5 h-5 text-gray-400 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span v-if="birthDate.length === 6" class="text-green-500">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                      <span v-else class="text-gray-400 text-sm">{{ birthDate.length }}/6</span>
                    </div>
                  </div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">예시: 930819 (YYMMDD 형식)</p>
                </div>

                <!-- 이름 입력 -->
                <div class="space-y-2">
                  <label
                    class="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    <svg
                      class="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    이름
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                  <div class="relative">
                    <input
                      type="text"
                      v-model="userName"
                      placeholder="홍길동"
                      class="form-input pl-10 pr-10 py-3 text-base font-medium"
                    />
                    <div
                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    >
                      <svg
                        class="w-5 h-5 text-gray-400 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span v-if="userName.trim().length > 0" class="text-green-500">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">실명을 입력해주세요</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 설문시작 버튼 -->
          <div class="text-center pt-2">
            <div class="relative">
              <button
                @click="startSurvey"
                :disabled="!isFormValid"
                class="group relative inline-flex items-center justify-center px-8 py-2 text-base font-bold text-white bg-gradient-to-r from-primary-600 to-primary-700 border border-transparent rounded-lg shadow-lg transition-all duration-300 hover:from-primary-700 hover:to-primary-800 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-600 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"
                ></div>
                <svg
                  class="w-4 h-4 mr-2 transition-transform duration-300 group-hover:scale-110 relative z-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span class="relative z-10">설문시작</span>
                <svg
                  v-if="isFormValid"
                  class="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1 relative z-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
            </div>
            <div
              v-if="!isFormValid"
              class="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
            >
              <div class="flex items-center justify-center space-x-2">
                <svg
                  class="w-3 h-3 text-amber-600 dark:text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <p class="text-amber-800 dark:text-amber-200 font-medium text-sm">
                  생년월일(6자리)과 이름을 모두 입력해주세요
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.index-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  overflow-y: auto;
  z-index: 50; /* Higher than sidebar and header */
}

.dark .index-page {
  background-color: rgb(17 24 39); /* dark:bg-gray-900 */
}
</style>
